import type { LitElement, ReactiveController } from 'lit';

import type { NSStripeService } from '../../../../../ns-services/ns-stripe-service.js';
import { MANDATE_TEXT_VERSION, setupIntentFromSearch } from '../../../../../helpers/auto-payments.js';
import {
  autopayChangeIntent,
  confirmAutopayChangeCard,
  type ChangedCard,
} from '../../../../../helpers/auto-payments-api.js';

/**
 * The new automatic payments card form (#25 phase 6), used by Change the automatic payments card and Automatic payment
 * failed, whose holder enters the new card on the card itself. `start` asks public-ledger/autopay/change-intent for a
 * SetupIntent on the company's automatic payments customer (for this job) and mounts Stripe's Payment Element into the
 * host's `stripe-payment-form` slot; `save` confirms it with Stripe, then public-ledger/autopay/confirm-change-card holds the
 * card, never live, with the mandate text version the host showed, records the card change (round 15: the account-signed
 * record its workflow then schedules) and completes the job. After a bank's redirect (3-D Secure) Stripe brings the browser back to the job naming the SetupIntent:
 * `returnFromBank` holds that card.
 *
 * A controller rather than an element, so the slot renders in the job card's own shadow root, as the Payment Element needs.
 * The host shows its own busy state and errors: `start`, `save` and `returnFromBank` throw what went wrong, and a refusal to
 * hold the card goes to `refused`; a held card goes to `held`, after the form is cleaned up.
 */
export class AutopayCardForm implements ReactiveController {
  // Set once the form is mounted; the host renders the Stripe slot while it is
  clientSecret: string | null = null;

  constructor(
    private readonly host: LitElement,
    private readonly options: {
      stripe: () => NSStripeService | undefined;
      token: () => string | undefined;
      // The job the card is entered on
      jobId: () => string | null | undefined;
      held: (card: ChangedCard) => void | Promise<void>;
      refused: (err: unknown) => void | Promise<void>;
    },
  ) {
    host.addController(this);
  }

  hostConnected() {}

  hostDisconnected() {
    this.clean();
  }

  /** Mounts the card form for a new SetupIntent. Throws when it can't be started. */
  async start() {
    const jobId = this.options.jobId();
    const token = this.options.token();
    if (!jobId || !token) return;
    try {
      const { clientSecret } = await autopayChangeIntent(token, jobId);
      this.clientSecret = clientSecret;
      this.host.requestUpdate();
      await this.host.updateComplete;
      const stripe = this.options.stripe();
      // The Stripe service is shared: a form another card left behind would otherwise be reused
      stripe?.cleanElements();
      await stripe?.mountStripeElement(this.host, clientSecret);
    } catch (err) {
      this.clientSecret = null;
      this.host.requestUpdate();
      throw err;
    }
  }

  /** Confirms the card with Stripe and holds it. Stripe shows its own refusal in the form (nothing is held then). */
  async save() {
    const stripe = this.options.stripe();
    if (!stripe) throw new Error('The card could not be saved. Please try again.');
    const setupIntentId = await stripe.confirmCardSetup();
    if (setupIntentId) await this.__hold(setupIntentId);
  }

  /** Whether a bank's redirect brought the browser back to the job with a card to hold. */
  returningFromBank(search: string = window.location.search): boolean {
    return setupIntentFromSearch(search) !== null;
  }

  /** Holds the card a bank's redirect returned to this job with, if any. Throws when the bank didn't confirm it. */
  async returnFromBank() {
    const returned = setupIntentFromSearch(window.location.search);
    if (!returned) return;
    const url = new URL(window.location.href);
    for (const key of ['setup_intent', 'setup_intent_client_secret', 'redirect_status']) url.searchParams.delete(key);
    window.history.replaceState(window.history.state, '', url.toString());
    if (!returned.succeeded) throw new Error('Your bank did not confirm the card. Please try again.');
    await this.__hold(returned.setupIntentId);
  }

  /** Removes the form, without holding anything. */
  clean() {
    this.options.stripe()?.cleanElements();
    if (this.clientSecret === null) return;
    this.clientSecret = null;
    this.host.requestUpdate();
  }

  // Holds the saved card as the company's next automatic payments card; the server completes the job a moment later
  private async __hold(setupIntentId: string) {
    const jobId = this.options.jobId();
    const token = this.options.token();
    if (!jobId || !token) return;
    let card: ChangedCard;
    try {
      card = await confirmAutopayChangeCard(token, { jobId, setupIntentId, mandateTextVersion: MANDATE_TEXT_VERSION });
    } catch (err) {
      await this.options.refused(err);
      return;
    }
    this.clean();
    await this.options.held(card);
  }
}
